import { X } from 'lucide-react';
import { useConfigSnapshotDetail } from '../hooks/useConfigSnapshots';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConfigRestoreControls from './ConfigRestoreControls';
import ConfigText from './ConfigText';

interface ConfigSnapshotViewerProps {
  deviceId: string;
  snapshotId: string;
  onClose: () => void;
}

export default function ConfigSnapshotViewer({
  deviceId,
  snapshotId,
  onClose,
}: ConfigSnapshotViewerProps) {
  const { data: snapshot, isLoading } = useConfigSnapshotDetail(deviceId, snapshotId);

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading snapshot…</p>;
  }

  if (!snapshot) {
    return (
      <Alert variant="destructive" className="text-xs">
        <AlertDescription>Could not load snapshot content.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="rounded-md border border-border-subtle bg-card p-3">
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className="text-xs font-bold">Configuration snapshot</p>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="font-normal text-[10px]">
            {snapshot.format}
          </Badge>
          <Badge variant="secondary" className="font-normal text-[10px]">
            {(snapshot.sizeBytes / 1024).toFixed(1)} KB
          </Badge>
          <Badge variant="secondary" className="font-normal text-[10px]">
            {new Date(snapshot.createdAt).toLocaleString()}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onClose}
            aria-label="Close snapshot viewer"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <ConfigRestoreControls
        deviceId={deviceId}
        snapshotId={snapshot.id}
        capturedAt={snapshot.createdAt}
      />

      <ConfigText
        text={snapshot.configText}
        className="max-h-96 overflow-auto rounded-md bg-muted/30 p-2 text-xs leading-relaxed font-mono"
      />
    </div>
  );
}
